import React from "react";
import { Card, CardHeader, InfoTooltip } from "../../../components";
import type { DispatchSummary } from "../domain/types";
import { SOURCE_TYPE_COLORS } from "../domain/types";

interface Props {
  dispatch: DispatchSummary;
}

function formatMWh(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)} TWh`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)} GWh`;
  return `${val.toFixed(0)} MWh`;
}

function BarSegment({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  if (pct < 0.5) return null;

  return (
    <div
      className="relative h-10 flex items-center justify-center text-body-sm font-medium text-white"
      style={{ width: `${pct}%`, backgroundColor: color, minWidth: pct > 3 ? undefined : "24px" }}
      title={`${label}: ${formatMWh(value)} (${pct.toFixed(1)}%)`}
    >
      {pct > 8 && (
        <span className="truncate px-1">
          {label} {pct.toFixed(0)}%
        </span>
      )}
    </div>
  );
}

export function LoadDispatchChart({ dispatch }: Props) {
  const total = dispatch.total_load_mwh;

  return (
    <Card>
      <CardHeader>Generation Mix</CardHeader>
      <div className="p-4 space-y-4">
        {/* Stacked bar */}
        <div className="flex rounded-lg overflow-hidden">
          <BarSegment
            label="Solar+Wind"
            value={dispatch.total_re_served_mwh}
            total={total}
            color={SOURCE_TYPE_COLORS.solar}
          />
          <BarSegment
            label="BESS"
            value={dispatch.total_bess_discharged_mwh}
            total={total}
            color={SOURCE_TYPE_COLORS.bess}
          />
          <BarSegment
            label="NG"
            value={dispatch.total_ng_gen_mwh}
            total={total}
            color={SOURCE_TYPE_COLORS.ng}
          />
          {(dispatch.total_unserved_mwh ?? 0) > 0 && (
            <BarSegment
              label="Unserved"
              value={dispatch.total_unserved_mwh!}
              total={total}
              color="#f97316"
            />
          )}
        </div>

        {/* Unserved warning */}
        {(dispatch.total_unserved_mwh ?? 0) > 0 && (
          <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-3">
            <svg className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-body-sm text-orange-300">
              {dispatch.hours_unserved} hours of unserved load ({formatMWh(dispatch.total_unserved_mwh!)}
              , peak {dispatch.max_unserved_mw?.toFixed(1)} MW). Consider adding capacity.
            </span>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-body-sm">
          <div>
            <span className="text-text-muted">Total Load</span>
            <div className="text-text-primary font-medium">
              {formatMWh(dispatch.total_load_mwh)}
            </div>
          </div>
          <div>
            <span className="text-text-muted">
              RE Coverage <InfoTooltip text="Percentage of total load served by RE (direct) + BESS discharge." position="bottom" />
            </span>
            <div className="text-quantum-400 font-medium">
              {dispatch.re_coverage_pct.toFixed(1)}%
            </div>
          </div>
          <div>
            <span className="text-text-muted">
              NG Capacity Factor <InfoTooltip text="NG generation as a fraction of maximum possible output (capacity x 8760 hours)." position="bottom" />
            </span>
            <div className="text-text-primary font-medium">
              {(dispatch.ng_capacity_factor * 100).toFixed(1)}%
            </div>
          </div>
          <div>
            <span className="text-text-muted">
              BESS Cycles <InfoTooltip text="Total energy discharged divided by battery capacity. Higher cycles = more utilization." position="bottom" />
            </span>
            <div className="text-text-primary font-medium">
              {dispatch.bess_cycles.toFixed(0)}
            </div>
          </div>
          <div>
            <span className="text-text-muted">RE Generated</span>
            <div className="text-text-primary font-medium">
              {formatMWh(dispatch.total_re_gen_mwh)}
            </div>
          </div>
          <div>
            <span className="text-text-muted">RE to Load</span>
            <div className="text-text-primary font-medium">
              {formatMWh(dispatch.total_re_served_mwh)}
            </div>
          </div>
          <div>
            <span className="text-text-muted">NG Generation</span>
            <div className="text-text-primary font-medium">
              {formatMWh(dispatch.total_ng_gen_mwh)}
            </div>
          </div>
          <div>
            <span className="text-text-muted">Curtailed</span>
            <div className="text-text-primary font-medium">
              {formatMWh(dispatch.total_curtailed_mwh)}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
